/**
 * The song card (#4279, Music Production Metadata FunctionalSpec §4–§5).
 *
 * Every song leaves the music factory with a `factory.json`. That file is the generator's
 * working state as well as a description of its output, and the two disagree: the reference
 * song is English trip-hop while its `suggested` block says Spanish highlife. The card is
 * the DESCRIPTIVE half, reduced once at ingest, and it is what every surface reads — admin,
 * the room panel, the companion and the playlist matcher — so no two can disagree about
 * what a song is. Provenance never appears in these types, so it cannot leak through them.
 */

/** Compact descriptive fields. `bpm` and `key` are what the song's BRIEF called for — stated, never measured. */
export interface SongCardFields {
  title: string;
  artist: string | null;
  genre: string | null;
  language: string | null;
  mood: string | null;
  theme: string | null;
  bpm: number | null;
  key: string | null;
  era: string | null;
  tags: string[];
  description: string | null;
  instrumental: boolean | null;
  /**
   * The tempo the audio actually PLAYS at and where beat 1 falls, measured from the FLAC by the factory
   * (`factory.json` `rhythm`, librosa). Unlike `bpm`, this is measured. Absent or null on a song measured before
   * 2026-11-04 or whose block did not validate. Optional so stored cards from before it stay valid.
   */
  rhythm?: SongRhythm | null;
}

/**
 * One straight beat grid. Within it beat n lands at `downbeatMs + n * 60000 / bpm`, typically within 8–30 ms.
 * A song has several when the beat jumps phase at a section change; between them there may be a short gap.
 */
export interface SongRhythmSegment {
  startMs: number;
  endMs: number;
  bpm: number;
  /** A beat 1 inside this segment. */
  downbeatMs: number;
  /**
   * Whether beat 1 can be told from the other beats of the bar here (confidence >= 0.2 and consistent). When not,
   * the beat positions are still right but beat 1 could be any beat: give every beat the same weight.
   */
  downbeatTrusted: boolean;
}

export interface SongRhythm {
  source: 'librosa-beat';
  /** Measured, duration-weighted over the segments. */
  bpm: number;
  beatsPerBar: number;
  segments: SongRhythmSegment[];
}

/**
 * Where one SUNG line falls in time, from the factory's forced alignment. Milliseconds from the start of the
 * track, like every other position a room reads.
 */
export interface SongLyricLineTiming {
  /** Where the line's first word is sung. This is the trustworthy end of the pair. */
  startMs: number;
  /**
   * Where its last word stops. NOT where the next line begins — a line that trails into an instrumental is given
   * the whole tail (20 s for one line, in the reference song), so a renderer must derive its own end from the
   * next line's start rather than take this as the length of the highlight.
   */
  endMs: number;
  /** The aligner's own confidence in this line, 0..1. */
  confidence: number;
}

/**
 * The factory's alignment for a song's lyrics (#4345). One entry per sung line, in the order the lines are stored —
 * section tags have none, because nothing sings them.
 *
 * The line TEXT is deliberately absent. It was proved equal to the stored lyrics at ingest, and both are served from
 * one row under one `version`, so they cannot come apart; storing it again would double a 20 KB field for nothing.
 * A reader still checks the COUNT, and falls back to estimating when it disagrees.
 */
export interface SongLyricTiming {
  /** Only `forced-align` is produced or trusted. The field exists so a future source is a value, not a guess. */
  source: 'forced-align';
  /** The aligner, for admin and for telling one production run's quality from another's. */
  model: string | null;
  /** The aligner's confidence over the whole track, 0..1. */
  confidence: number;
  lines: SongLyricLineTiming[];
}

/** What a room may be told about a song. Immutable under its `version`, so it can be cached for ever. */
export interface SongCard extends SongCardFields {
  trackId: string;
  /** Increases whenever the card's content changes. */
  version: number;
  /** The first chorus, for singing along and for quoting. Null for an instrumental or a song with no chorus tag. */
  hook: string | null;
  /** The FLAC's own length — authoritative, whatever the factory file said. */
  durationMs: number;
  /** Whether full lyrics are stored. They are fetched separately, when someone opens them. */
  hasLyrics: boolean;
  /** So that "who is this artist?" has a true answer and not an invented biography (P10). */
  origin: string;
}

export const SONG_CARD_ORIGIN = 'A Telegames original. The artist name is a persona of the Telegames music factory, not a real person or band.';

/** §4.2. Strings are capped by code points after scrubbing; a file is refused above `fileBytes`. */
export const SONG_CARD_CAPS = Object.freeze({
  title: 255, artist: 255, genre: 80, language: 80, era: 80, key: 80, mood: 200, theme: 500,
  description: 2000, rationale: 2000, style: 8000, lyrics: 20000, score: 64000, tag: 80, tags: 32,
  /**
   * The most rows of a lyric anyone reads: `lyricRows` stops here, so a file that is not lyrics cannot become a
   * thousand elements, and ingest and the overlay agree on which rows exist. The number of TIMINGS is bounded by
   * the same cap, because there is one per sung row and sung rows are a subset.
   *
   * It is not the binding constraint in practice. `fileBytes` is: the aligner writes per-word timings too, about
   * 840 bytes a line in the reference song, so a factory.json crosses 256 KB somewhere around 300 sung lines and
   * is refused whole, before any of this is reached.
   */
  timingLines: 400,
  /** Beat-grid segments kept per song. The factory measured at most 17 across 122 songs. */
  rhythmSegments: 32,
  bpmMin: 20, bpmMax: 400, fileBytes: 262144, hook: 300, durationToleranceSec: 0.25,
});

/**
 * What a section marker is, and which rows a stored lyric HAS — in one place, because two of them is a bug.
 *
 * Ingest proves the timings line up with the lyrics by walking the sung rows; the overlay applies them by walking
 * the sung rows. If those two walks disagree about a single strange line — `[a[b]`, a blank held by a stray space —
 * every timing after it is applied to the wrong line, silently, on that one song. So both import these.
 */
export const isLyricSectionTag = (line: string): boolean => /^\s*\[[^\]]*\]\s*$/.test(line);
/**
 * The rows of a stored lyric: trimmed, blanks dropped, and CAPPED — section tags included, since they are shown.
 *
 * The cap belongs here rather than at each call site, and that is the whole point. Capping total rows on one side
 * and sung rows on the other is the same off-by-one this module exists to prevent, wearing a different hat: 381
 * sung lines among 401 rows passes an ingest check on sung rows and then loses one to an overlay that cuts at 400
 * rows first, so the counts disagree and the song silently falls back to the estimate while admin reports it aligned.
 */
export const lyricRows = (lyrics: string): string[] =>
  lyrics.split(/\r\n|\r|\n/).map(line => line.trim()).filter(Boolean).slice(0, SONG_CARD_CAPS.timingLines);
/** The sung rows alone, in order. This is the order `SongLyricTiming.lines` is given in, and what its length must equal. */
export const sungLyricRows = (lyrics: string): string[] => lyricRows(lyrics).filter(line => !isLyricSectionTag(line));

/**
 * The shape of what the card routes serve, and part of what a response is immutable UNDER: (track, card version, format).
 * Rewording the origin note, or serving one more field, changes the body without changing any card — so it MUST raise this,
 * or a browser that already holds the old body keeps it for a year.
 *
 * 2 (#4345): the lyrics body carries `timing`. A browser holding a 1 body would otherwise keep a timing-less answer
 * for a song that now has one, for a year, and sing along by estimate under a card that says otherwise.
 *
 * 3 (2026-11-04): the card carries `rhythm`, the measured beat grid the companion grooves to.
 */
export const SONG_CARD_FORMAT = 3;
/**
 * Where a room reads a card, or its lyrics, once the queue has told it the version. The key is in the PATH and not in a query
 * string: beta's web app sits behind a CDN, and whether a CDN's cache key includes the query string is a setting somewhere else.
 */
export const songCardPath = (trackId: string, version: number) => `/api/music/tracks/${encodeURIComponent(trackId)}/card/${version}-${SONG_CARD_FORMAT}`;
export const songLyricsPath = (trackId: string, version: number) => `/api/music/tracks/${encodeURIComponent(trackId)}/lyrics/${version}-${SONG_CARD_FORMAT}`;
